import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import useTable from 'components/controls/useTable'
import { Building2, MapPin, XCircle, Upload, Truck } from 'lucide-react'
import * as _ from 'styles/helpers'
import Popup from 'components/Popup'
import Layout from 'components/Layout'
import MyDropzone from 'components/MyDropzone/MyDropzone'
import ListAction from 'features/Actions/components/ListAction'
import {
	createBulkLocations,
	createLocation,
	initializeActiveLocations,
	initializeLocations,
	initializeMyCenters,
	patchLocation,
	updateLocation,
} from 'slices/locationSlice'
import {
	deleteAllLocations,
	deleteOneLocation,
	initializeBulkLocations,
	updateBulkLocation,
} from 'slices/bulkLocationSlice'
import notify from 'components/ui/notify'
import { initializeApiKey } from 'slices/apiKeysSlice'
import Spinner from 'components/Spinner'
import BulkLocationEditPopup from 'components/popups/BulkLocationEditPopup'
import BulkFleetAddPopup from 'components/popups/BulkFleetAddPopup'
import BulkLocationsAddPopup from 'components/popups/BulkLocationsAddPopup'
import BulkTenantsAddPopup from 'components/popups/BulkTenantsAddPopup'
import Button from 'components/Button'
import LocationAddNew from 'screens/locations/components/LocationAddNew'
import { ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'

const headerCells = [
	{ id: 'assetName', label: 'Location' },
	{ id: 'retailer', label: 'Retailer' },
	{ id: 'address', label: 'Address' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State' },
	{ id: 'postalCode', label: 'Zip' },
	{ id: 'delete', label: 'Delete', sortable: false },
]

const BulkLoadLocations = ({ userId }) => {
	const [filterFn, setFilterFn] = useState({
		fn: items => {
			return items
		},
	})
	const dispatch = useDispatch()
	const searchInputRef = useRef(null)

	const [openModifyPopup, setOpenModifyPopup] = useState(false)
	const [openAddLocationPopup, setOpenAddLocationPopup] = useState(false)
	const [openPostLocationsPopup, setOpenPostLocationsPopup] = useState(false)
	const [openPostTenantsPopup, setOpenPostTenantsPopup] = useState(false)
	const [openPostFleetPopup, setOpenPostFleetPopup] = useState(false)

	const [popupTitle, setPopupTitle] = useState('')
	const [searchInputValue, setSearchInputValue] = useState('')
	const [fileName, setFileName] = useState(null)
	const [recordForEdit, setRecordForEdit] = useState(null)

	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const { entity: apiKeyState } = useSelector(state => state.apiKey)
	const apiKey = apiKeyState.key

	const { entities: locations, ids: locationIds, isLoading, isInitialized } = useSelector(
		state => state.bulkLocations,
	)
	const records = locationIds.map(id => locations[id])

	const { entities, ids: centerIds } = useSelector(state => state.locations)
	const centers = centerIds.map(id => entities[id])

	const myCenters = centers.map(center => {
		return {
			selectId: center._id,
			selectValue: center.assetName,
		}
	})

	useEffect(() => {
		dispatch(initializeMyCenters())
		if (!isInitialized) {
			dispatch(initializeBulkLocations())
		}
		dispatch(initializeApiKey('REACT_APP_GOOGLE_API_KEY'))
	}, [dispatch, isInitialized])

	const hasRows = records ? records.length : null
	const hasCenters = myCenters ? myCenters.length : null

	const postSummary = {
		recordCount: records.length,
		isActive: false,
		isPOIOnly: false,
		isTenantOnly: false,
		fleetId: '',
	}

	const { recordsAfterPagingAndSorting } = useTable(
		records,
		headerCells,
		filterFn,
		true
	)

	const handleSort = columnId => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const getNestedValue = (obj, path) => {
		return path.split('.').reduce((acc, key) => acc?.[key], obj)
	}

	const filteredRecords = recordsAfterPagingAndSorting()

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	const SortIcon = ({ columnId }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-primary" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-primary" />
		)
	}

	const handleSearch = e => {
		const { value } = e.target
		setSearchInputValue(value)
		setFilterFn({
			fn: items => {
				if (value === '') return items
				return items.filter(x => x.assetName.toLowerCase().includes(value.toLowerCase()))
			},
		})
		// set focus to search input after rerender
		setTimeout(() => {
			searchInputRef.current.focus()
		}, 0)
	}

	const actionPostLocations = () => {
		setPopupTitle('Post as Locations:')
		setOpenPostLocationsPopup(true)
	}
	const actionPostFleet = () => {
		setOpenPostFleetPopup(true)
	}

	const actionPostTenants = () => {
		if (hasCenters > 0) {
			setPopupTitle('Post as Tenants:')
			setOpenPostTenantsPopup(true)
			// notify({
			// 	title: `Post Retail Center Locations!`,
			// 	text: `This will post the current list of locations as retail tenant locations.`,
			// 	icon: 'success',
			// 	button: 'Ok',
			// })
		} else {
			notify({
				title: `No Retail Centers!`,
				text: `You must first create a retail center before you can post this list as tenants.`,
				icon: 'warning',
				button: 'Ok',
			})
			setPopupTitle('Add Retail Center Location')
			setOpenAddLocationPopup(true)
		}
	}
	const handleOpenEditPopup = location => {
		setPopupTitle('Edit Location')
		setRecordForEdit(location)
		setOpenModifyPopup(true)
	}

	const handleAddLocation = async (location, resetForm) => {
		const newLocation = location

		newLocation.owner = userId

		if (newLocation.locationType) {
			const locationType = newLocation.locationType.toLocaleLowerCase()
			const newAssetType = locationType.split(' ')

			if (newAssetType[1]) newLocation.assetType = newAssetType[1]
		}
		await dispatch(createLocation({ newLocation }))
		dispatch(initializeActiveLocations())
		resetForm()
		setOpenAddLocationPopup(false)
	}

	const handleEditLocation = async newLocation => {
		await dispatch(updateBulkLocation(newLocation))

		setOpenModifyPopup(false)
	}

	const handlePostBulkLocations = async activeStatus => {
		const locations = records

		await dispatch(createBulkLocations(locations))

		setOpenPostLocationsPopup(false)
	}

	const handlePostFleetLocations = async postSummary => {
		await dispatch(updateBulkLocation(records))

		setOpenModifyPopup(false)
	}

	const handlePostCenterTenants = async retailCenter => {
		await dispatch(updateBulkLocation(retailCenter))

		setOpenModifyPopup(false)
	}

	const handleClearList = () => {
		notify({
			title: `Clear List!`,
			text: `This will clear the current import list without posting the new locations.`,
			icon: 'success',
			buttons: {
				confirm: {
					text: 'Confirm',
					value: true,
					visible: true,
					className: '',
					closeModal: true,
				},
				cancel: {
					text: 'Cancel',
					value: null,
					visible: true,
					className: '',
					closeModal: true,
				},
			},
		}).then(willDelete => {
			if (willDelete) {
				notify({
					title: `List Cleared!`,
					text: `The current list has been cleard.`,
					icon: 'success',
				})
				dispatch(deleteAllLocations())
			}
		})
		dispatch(initializeBulkLocations())

		// dispatch(uploadCenterTenants(locationId, tenants))
	}

	const handleDeleteDetail = async location => {
		if (await toast.confirm({ title: 'Delete location', message: 'Delete this location?' })) {
			await dispatch(deleteOneLocation(location._id))
			dispatch(initializeBulkLocations())
		}
	}

	return (
		<div>
			<Layout>
				<Layout.Body>
					<div className="grid grid-cols-12 gap-6">
						<div className="col-span-9">
							<div>
								<Layout.PageToolbarNoIcon
									buttonText="Clear List"
									buttonOnClick={handleClearList}
									searchInputRef={searchInputRef}
									searchInputValue={searchInputValue}
									searchInputOnChange={handleSearch}
								/>
							</div>
							{!hasRows ? (
								<MyDropzone apiKey={apiKey} />
							) : isLoading ? (
								<Spinner containerHeight="50vh" />
							) : (
								<div className="TableContent">
									<Table>
										<TableHeader>
											<TableRow className="border-b-2 border-[#027fff] hover:bg-transparent">
												{headerCells.map(cell => (
													<TableHead
														key={cell.id}
														className={
															cell.sortable !== false
																? 'cursor-pointer select-none text-gray-700 text-xs font-semibold whitespace-nowrap'
																: 'text-gray-700 text-xs font-semibold whitespace-nowrap'
														}
														onClick={cell.sortable !== false ? () => handleSort(cell.id) : undefined}
													>
														<span className="inline-flex items-center">
															{cell.label}
															{cell.sortable !== false && <SortIcon columnId={cell.id} />}
														</span>
													</TableHead>
												))}
											</TableRow>
										</TableHeader>
										<TableBody>
											{paginatedRecords.map(location => (
												<TableRow
													key={location._id}
													className="h-7 hover:bg-[#fffbf2] cursor-pointer"
												>
													<TableCell className="py-1 text-sm" onClick={() => handleOpenEditPopup(location)}>
														{location.assetName}
													</TableCell>
													<TableCell className="py-1 text-sm" onClick={() => handleOpenEditPopup(location)}>
														{location.retailer}
													</TableCell>
													<TableCell className="py-1 text-sm" onClick={() => handleOpenEditPopup(location)}>
														{location.address}
													</TableCell>
													<TableCell className="py-1 text-sm" onClick={() => handleOpenEditPopup(location)}>
														{location.city}{' '}
													</TableCell>
													<TableCell className="py-1 text-sm" onClick={() => handleOpenEditPopup(location)}>
														{location.stateProvince}
													</TableCell>
													<TableCell className="py-1 text-sm" onClick={() => handleOpenEditPopup(location)}>
														{location.postalCode}
													</TableCell>
													<TableCell className="py-1 text-sm">
														<span
															onClick={() => handleDeleteDetail(location)}
															className="inline-flex justify-center items-center pl-4 cursor-pointer text-red-500 hover:text-red-700 text-sm border-none"
															aria-label="Delete"
															title="Delete"
														>
															<XCircle className="w-4 h-4" />
														</span>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
									<TablePagination
										page={page}
										rowsPerPage={rowsPerPage}
										count={sortedRecords.length}
										onPageChange={setPage}
										onRowsPerPageChange={setRowsPerPage}
									/>
								</div>
							)}
						</div>
						<div className="col-span-3">
							<Layout.SidebarItem title="Actions" hasNoMaxHeight>
								<ul>
									<li className="actionList__item">
										<ListAction
											handleAction={actionPostLocations}
											actionLabel={'Post as Locations'}
											actionIcon={<MapPin className="w-4 h-4" />}
										/>
									</li>
									{/* <li className="actionList__item">
										<ListAction
											handleAction={actionPostFleet}
											actionLabel={'Post as Fleet'}
											actionIcon={<FaRegThumbsUp />}
										/>
									</li> */}
									<li className="actionList__item">
										<ListAction
											handleAction={actionPostTenants}
											actionLabel={'Post as Tenants'}
											actionIcon={<Building2 className="w-4 h-4" />}
										/>
									</li>
								</ul>
							</Layout.SidebarItem>
						</div>
					</div>
				</Layout.Body>
			</Layout>

			{openModifyPopup && (
				<Popup title={popupTitle} openPopup={openModifyPopup} setOpenPopup={setOpenModifyPopup} icon={<MapPin className="w-5 h-5" />} description="Edit location details">
					<BulkLocationEditPopup editLocation={handleEditLocation} recordForEdit={recordForEdit} />
				</Popup>
			)}

			{openPostLocationsPopup && (
				<Popup
					title={popupTitle}
					openPopup={openPostLocationsPopup}
					setOpenPopup={setOpenPostLocationsPopup}
					icon={<Upload className="w-5 h-5" />}
					description="Bulk import locations"
				>
					<BulkLocationsAddPopup
						handlePostBulkLocations={handlePostBulkLocations}
						locationCount={records.length}
					/>
				</Popup>
			)}

			{openPostTenantsPopup && (
				<Popup
					title={popupTitle}
					openPopup={openPostTenantsPopup}
					setOpenPopup={setOpenPostTenantsPopup}
					icon={<Building2 className="w-5 h-5" />}
					description="Bulk add tenants"
				>
					<BulkTenantsAddPopup
						myCenters={myCenters}
						handlePostFleetLocations={handlePostFleetLocations}
					/>
				</Popup>
			)}

			{openAddLocationPopup && (
				<Popup
					title={popupTitle}
					openPopup={openAddLocationPopup}
					setOpenPopup={setOpenAddLocationPopup}
				>
					<LocationAddNew locationAdd={handleAddLocation} popupGuide="true" />
				</Popup>
			)}
		</div>
	)
}

export default BulkLoadLocations
